import React, { useEffect, useRef, useState } from 'react';
import Modal from './Modal.mjs';
import ClosingX from './ClosingX.jsx';
import Spinner from './Spinner.mjs';
import WaveformPlayer from './shared/WaveformPlayer.jsx';
import * as DenoiseService from '../services/DenoiseService.mjs';

// afftdn defaults (mirror the backend's safe defaults).
const DEFAULT_PARAMS = { nr: 12, nf: -50, nt: 'w', tn: false };

// Friendly one-click presets that set the reduction strength.
const PRESETS = [
    { key: 'light', label: 'Light', nr: 6 },
    { key: 'medium', label: 'Medium', nr: 12 },
    { key: 'strong', label: 'Strong', nr: 24 },
];

const NOISE_TYPES = [
    { value: 'w', label: 'White / broadband (hiss, hum, fans)' },
    { value: 'v', label: 'Vinyl (crackle)' },
    { value: 's', label: 'Shellac (old recordings)' },
];

const sameParams = (a, b) =>
    !!a && !!b && a.nr === b.nr && a.nf === b.nf && a.nt === b.nt && a.tn === b.tn;

const SCOPED_CSS = `
.dn-slider { width: 100%; accent-color: var(--qd-accent); cursor: pointer; }
.dn-preset { transition: background var(--qd-transition-fast), border-color var(--qd-transition-fast), color var(--qd-transition-fast); }
.dn-preset:hover { background: var(--qd-surface-3); }
.dn-btn { transition: filter var(--qd-transition-fast), transform var(--qd-transition-fast); }
.dn-btn:not(:disabled):hover { filter: brightness(1.06); }
.dn-btn:not(:disabled):active { transform: translateY(1px); }
`;

const DenoiseModal = ({ file, originalUrl, canApply = true, onApplied, onClose }) => {
    const [params, setParams] = useState(DEFAULT_PARAMS);

    const [previewUrl, setPreviewUrl] = useState(null);
    const [previewParams, setPreviewParams] = useState(null); // params the current preview was made with
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);

    const [applying, setApplying] = useState(false);
    const [applyError, setApplyError] = useState(null);
    const [applied, setApplied] = useState(false);

    const pollRef = useRef(null);
    const applyingRef = useRef(false); // synchronous re-entry guard (state is async)

    // Clear the poll timer on unmount.
    useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

    const setParam = (key, value) => {
        setParams((prev) => ({ ...prev, [key]: value }));
    };

    const previewStale = previewUrl && !sameParams(params, previewParams);

    const handlePreview = async () => {
        if (previewLoading) return;
        setPreviewLoading(true);
        setPreviewError(null);
        try {
            const { url, params: serverParams } = await DenoiseService.requestDenoisePreview(file.id, params);
            setPreviewUrl(url);
            // Use the server's clamped params so the staleness check matches exactly.
            setPreviewParams(serverParams || params);
        } catch (err) {
            setPreviewError(err?.response?.data?.message || 'Could not generate a preview.');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleApply = async () => {
        // Guard on a ref, not the async `applying` state, so a fast double-trigger
        // can't slip through before React re-renders the disabled button.
        if (applyingRef.current || !canApply) return;
        applyingRef.current = true;
        setApplying(true);
        setApplyError(null);
        setApplied(false);
        try {
            const { jobId } = await DenoiseService.applyDenoise(file.id, params);

            // Never leave a prior interval running if somehow re-entered.
            if (pollRef.current) clearInterval(pollRef.current);
            pollRef.current = setInterval(async () => {
                try {
                    const status = await DenoiseService.getDenoiseStatus(jobId);
                    if (status.status === 'done' || status.status === 'failed') {
                        clearInterval(pollRef.current);
                        pollRef.current = null;
                        applyingRef.current = false;
                        setApplying(false);
                        if (status.status === 'done') {
                            setApplied(true);
                            setTimeout(() => onApplied?.(status), 900);
                        } else {
                            setApplyError(status.error || 'The denoise failed.');
                        }
                    }
                } catch (err) {
                    clearInterval(pollRef.current);
                    pollRef.current = null;
                    applyingRef.current = false;
                    setApplying(false);
                    setApplyError('Lost contact with the denoise job.');
                }
            }, 2000);
        } catch (err) {
            applyingRef.current = false;
            setApplying(false);
            setApplyError(err?.response?.data?.message || 'Could not start the denoise.');
        }
    };

    const activePreset = PRESETS.find((p) => p.nr === params.nr)?.key;

    return (
        <Modal className="denoiseModal" onClose={onClose}>
            <style>{SCOPED_CSS}</style>
            <div style={styles.wrap}>
                <div style={styles.header}>
                    <div>
                        <h1 style={styles.title}>🧹 Denoise audio</h1>
                        <span style={styles.subtitle}>{file?.metadata?.name}</span>
                    </div>
                    <ClosingX onClick={onClose} />
                </div>

                <div style={styles.body}>
                    {/* ── Options ──────────────────────────────────────────── */}
                    <div style={styles.section}>
                        <div style={styles.sectionTitle}>Settings</div>

                        <div style={styles.presetRow}>
                            {PRESETS.map((p) => (
                                <button
                                    key={p.key}
                                    className="dn-preset"
                                    onClick={() => setParam('nr', p.nr)}
                                    style={{
                                        ...styles.preset,
                                        ...(activePreset === p.key ? styles.presetActive : {}),
                                    }}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <label style={styles.field}>
                            <span style={styles.fieldLabel}>
                                Noise reduction strength
                                <span style={styles.readout}>{params.nr} dB</span>
                            </span>
                            <input
                                type="range" className="dn-slider"
                                min="1" max="48" step="1"
                                value={params.nr}
                                onChange={(e) => setParam('nr', Number(e.target.value))}
                            />
                            <span style={styles.hint}>Higher removes more noise but can sound watery.</span>
                        </label>

                        <label style={styles.field}>
                            <span style={styles.fieldLabel}>
                                Noise floor
                                <span style={styles.readout}>{params.nf} dB</span>
                            </span>
                            <input
                                type="range" className="dn-slider"
                                min="-80" max="-20" step="1"
                                value={params.nf}
                                onChange={(e) => setParam('nf', Number(e.target.value))}
                            />
                            <span style={styles.hint}>Level below which audio is treated as noise.</span>
                        </label>

                        <label style={styles.field}>
                            <span style={styles.fieldLabel}>Noise type</span>
                            <select
                                value={params.nt}
                                onChange={(e) => setParam('nt', e.target.value)}
                                style={styles.select}
                            >
                                {NOISE_TYPES.map((t) => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </label>

                        <label style={styles.toggle}>
                            <input
                                type="checkbox"
                                checked={params.tn}
                                onChange={(e) => setParam('tn', e.target.checked)}
                                style={{ accentColor: 'var(--qd-accent)', width: 16, height: 16 }}
                            />
                            Adaptive noise tracking <span style={styles.hint}>(adjusts as the noise changes over time)</span>
                        </label>
                    </div>

                    {/* ── Preview ──────────────────────────────────────────── */}
                    <div style={styles.section}>
                        <div style={styles.previewHeader}>
                            <div style={styles.sectionTitle}>Preview</div>
                            <button
                                className="dn-btn"
                                onClick={handlePreview}
                                disabled={previewLoading}
                                style={{ ...styles.previewBtn, opacity: previewLoading ? 0.7 : 1, cursor: previewLoading ? 'default' : 'pointer' }}
                            >
                                {previewLoading
                                    ? (<><Spinner inline size={14} /> Generating…</>)
                                    : (previewUrl ? '↻ Re-preview' : '▶ Generate preview')}
                            </button>
                        </div>

                        {previewError && <div style={styles.errorBox}>{previewError}</div>}
                        {previewStale && !previewLoading && (
                            <div style={styles.staleBox}>Settings changed — re-generate the preview to hear them.</div>
                        )}

                        {originalUrl && (
                            <div style={styles.playerBlock}>
                                <span style={styles.playerLabel}>Original</span>
                                <WaveformPlayer src={originalUrl} className="media-player" />
                            </div>
                        )}
                        {previewUrl ? (
                            <div style={styles.playerBlock}>
                                <span style={styles.playerLabel}>Denoised preview (first 10s)</span>
                                <WaveformPlayer src={previewUrl} className="media-player" />
                            </div>
                        ) : (
                            <div style={styles.emptyPreview}>
                                Generate a preview to compare the denoised result against the original.
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Footer / apply ───────────────────────────────────────── */}
                <div style={styles.footer}>
                    {!canApply && (
                        <div style={styles.warn}>You need editor access to apply denoise to this file.</div>
                    )}
                    {applyError && <div style={styles.errorBox}>{applyError}</div>}
                    {applying && (
                        <div style={styles.progress}>
                            <Spinner inline size={14} /> Denoising the full file… this can take a moment.
                        </div>
                    )}
                    {applied && (
                        <div style={styles.success}>✓ Denoised. Saved as a new version (the original is kept in version history).</div>
                    )}

                    <div style={styles.footerActions}>
                        <button className="dn-btn" onClick={onClose} style={styles.cancelBtn}>
                            {applied ? 'Close' : 'Cancel'}
                        </button>
                        <button
                            className="dn-btn"
                            onClick={handleApply}
                            disabled={!canApply || applying || applied}
                            style={{
                                ...styles.applyBtn,
                                opacity: (!canApply || applying || applied) ? 0.5 : 1,
                                cursor: (!canApply || applying || applied) ? 'not-allowed' : 'pointer',
                            }}
                            title="Apply denoise to the whole file and save it as a new version"
                        >
                            {applying ? 'Applying…' : 'Apply denoise'}
                        </button>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

const styles = {
    wrap: {
        display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-4)', maxWidth: 720, width: '80vw',
        maxHeight: '82vh', fontFamily: 'var(--qd-font-sans)',
    },
    header: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--qd-space-3)' },
    title: { margin: 0, fontSize: 'var(--qd-text-xl)', color: 'var(--qd-text)' },
    subtitle: { fontSize: 'var(--qd-text-sm)', color: 'var(--qd-text-muted)' },
    body: { display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-5)', overflowY: 'auto', paddingRight: 'var(--qd-space-1)' },
    section: {
        display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-3)',
        background: 'var(--qd-surface-2)', border: '1px solid var(--qd-border)', borderRadius: 'var(--qd-radius-lg)', padding: 'var(--qd-space-4)',
    },
    sectionTitle: { fontSize: 'var(--qd-text-base)', fontWeight: 'var(--qd-weight-bold)', color: 'var(--qd-text)' },
    presetRow: { display: 'flex', gap: 'var(--qd-space-2)' },
    preset: {
        flex: 1, background: 'var(--qd-surface)', border: '1px solid var(--qd-border)', borderRadius: 'var(--qd-radius-md)',
        padding: '8px 0', fontSize: 'var(--qd-text-sm)', fontWeight: 'var(--qd-weight-semibold)', color: 'var(--qd-text-muted)', cursor: 'pointer',
    },
    presetActive: { background: 'var(--qd-accent-subtle)', borderColor: 'var(--qd-accent)', color: 'var(--qd-accent-text)' },
    field: { display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-1)' },
    fieldLabel: {
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        fontSize: 'var(--qd-text-sm)', fontWeight: 'var(--qd-weight-semibold)', color: 'var(--qd-text)',
    },
    readout: { fontVariantNumeric: 'tabular-nums', color: 'var(--qd-accent-text)', fontWeight: 'var(--qd-weight-bold)' },
    hint: { fontSize: 'var(--qd-text-2xs)', color: 'var(--qd-text-subtle)' },
    select: {
        border: '1px solid var(--qd-border)', borderRadius: 'var(--qd-radius-md)', padding: '8px 10px', fontSize: 'var(--qd-text-sm)',
        color: 'var(--qd-text)', background: 'var(--qd-surface)', cursor: 'pointer',
    },
    toggle: { display: 'flex', alignItems: 'center', gap: 'var(--qd-space-2)', fontSize: 'var(--qd-text-sm)', color: 'var(--qd-text)', cursor: 'pointer' },
    previewHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
    previewBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 'var(--qd-space-2)',
        background: 'var(--qd-accent)', color: 'var(--qd-text-on-accent)', border: 'none', borderRadius: 'var(--qd-radius-md)',
        padding: '8px 14px', fontWeight: 'var(--qd-weight-semibold)', fontSize: 'var(--qd-text-sm)',
    },
    playerBlock: { display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-1)' },
    playerLabel: { fontSize: 'var(--qd-text-xs)', fontWeight: 'var(--qd-weight-bold)', color: 'var(--qd-text-muted)', textTransform: 'uppercase', letterSpacing: 0.3 },
    emptyPreview: {
        fontSize: 'var(--qd-text-sm)', color: 'var(--qd-text-subtle)', textAlign: 'center', padding: '18px 12px',
        background: 'var(--qd-surface)', border: '1px dashed var(--qd-border)', borderRadius: 'var(--qd-radius-md)',
    },
    footer: { display: 'flex', flexDirection: 'column', gap: 'var(--qd-space-3)', borderTop: '1px solid var(--qd-border)', paddingTop: 'var(--qd-space-4)' },
    footerActions: { display: 'flex', justifyContent: 'flex-end', gap: 'var(--qd-space-3)' },
    cancelBtn: {
        background: 'var(--qd-surface)', border: '1px solid var(--qd-border)', borderRadius: 'var(--qd-radius-md)',
        padding: '10px 18px', fontWeight: 'var(--qd-weight-semibold)', fontSize: 'var(--qd-text-base)', color: 'var(--qd-text-muted)', cursor: 'pointer',
    },
    applyBtn: {
        background: 'var(--qd-success)', color: 'var(--qd-text-on-accent)', border: 'none', borderRadius: 'var(--qd-radius-md)',
        padding: '10px 20px', fontWeight: 'var(--qd-weight-bold)', fontSize: 'var(--qd-text-base)',
    },
    warn: { fontSize: 'var(--qd-text-sm)', color: 'var(--qd-warning)', background: 'var(--qd-warning-subtle)', border: '1px solid var(--qd-warning)', borderRadius: 'var(--qd-radius-md)', padding: '8px 12px' },
    errorBox: { fontSize: 'var(--qd-text-sm)', color: 'var(--qd-danger)', background: 'var(--qd-danger-subtle)', border: '1px solid var(--qd-danger)', borderRadius: 'var(--qd-radius-md)', padding: '10px 12px' },
    staleBox: { fontSize: 'var(--qd-text-sm)', color: 'var(--qd-accent-text)', background: 'var(--qd-accent-subtle)', border: '1px solid var(--qd-accent)', borderRadius: 'var(--qd-radius-md)', padding: '8px 12px' },
    progress: { display: 'flex', alignItems: 'center', gap: 'var(--qd-space-2)', fontSize: 'var(--qd-text-sm)', color: 'var(--qd-accent-text)', fontWeight: 'var(--qd-weight-semibold)' },
    success: { fontSize: 'var(--qd-text-sm)', color: 'var(--qd-success)', fontWeight: 'var(--qd-weight-semibold)' },
};

export default DenoiseModal;
